import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import LoginPage from './pages/LoginPage';
import MedicalDataDashboard from './components/Dashboard/MedicalDataDashboard';
import DashboardLayout from './components/Layout/DashboardLayout';
import { NavigationProvider } from './components/Navigation/NavigationContext';
import { MedicalReservationListPage } from './pages/MedicalReservationListPage';
import { OutOfHoursMedicalReservationListPage } from './pages/OutOfHoursMedicalReservationListPage';
import { DoctorShiftUploadPage } from './pages/DoctorShiftUploadPage';
import { DoctorShiftListPage } from './pages/DoctorShiftListPage';
import { DoctorListPage } from './pages/DoctorListPage';
import { DoctorShiftRuleListPage } from './presentation/pages/DoctorShiftRuleList/DoctorShiftRuleListPage';
import { MonthlyShiftSlotSetupPage } from './presentation/pages/MonthlyShiftSlotSetup/page/MonthlyShiftSlotSetupPage';
import { MonthlyShiftSlotManagerPage } from './presentation/pages/MonthlyShiftSlotManager';
import { MonthlyDoctorsShiftPage } from './presentation/pages/MonthlyDoctorsShift';
import { ReservationSlotManagementPage } from './presentation/pages/ReservationSlotManagement';
import { MedicalReservationsUploadPage } from './presentation/pages/MedicalReservationsUpload';
import UserManagementPage from './pages/UserManagementPage';
import DesignHarnessPage from './pages/DesignHarnessPage';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, AdminRoute } from './components/Auth/ProtectedRoute';

// Material-UIテーマ（女性向けの温かみのあるデザイン）
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f8f9ff',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", "Noto Sans JP", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
  },
});

// 開発中機能のプレースホルダーコンポーネント
const ComingSoon: React.FC<{ feature: string }> = ({ feature }) => (
  <Box sx={{ 
    display: 'flex', 
    flexDirection: 'column',
    alignItems: 'center', 
    justifyContent: 'center', 
    height: '60vh',
    textAlign: 'center'
  }}>
    <Typography variant="h4" gutterBottom>
      {feature}
    </Typography>
    <Typography variant="body1" color="text.secondary">
      この機能は現在開発中です
    </Typography>
  </Box>
);

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <NavigationProvider>
            <Routes>
              {/* ログインページ */}
              <Route path="/login" element={<LoginPage />} />
              {process.env.NODE_ENV === 'development' && <Route path="/design-harness" element={<DesignHarnessPage />} />}
              
              {/* 認証が必要なルート */}
              <Route element={<ProtectedRoute />}>
                <Route path="/design-harness" element={<DesignHarnessPage />} />
                {/* メインアプリケーション（レイアウト付き） */}
                <Route element={<DashboardLayout />}>
                  {/* ダッシュボード */}
                  <Route path="/dashboard" element={<MedicalDataDashboard />} />
                  
                  {/* 予約管理 */}
                  <Route path="/appointments/upload" element={<MedicalReservationsUploadPage />} />
                  <Route path="/appointments/list" element={<MedicalReservationListPage />} />
                  <Route path="/appointments/out-of-hours" element={<OutOfHoursMedicalReservationListPage />} />

                  {/* 医師管理 */}
                  {/* 医師一覧・シフトルールは管理者のみアクセス可能 */}
                  <Route element={<AdminRoute />}>
                    <Route path="/settings/users" element={<UserManagementPage />} />
                    <Route path="/doctors/list" element={<DoctorListPage />} />
                    <Route path="/doctors/shift-rules" element={<DoctorShiftRuleListPage />} />
                    <Route path="/doctors/monthly-shift-slots" element={<MonthlyShiftSlotSetupPage />} />
                    <Route path="/doctors/shifts/manager" element={<MonthlyShiftSlotManagerPage />} />
                    <Route path="/doctors/shifts/monthly" element={<MonthlyDoctorsShiftPage />} />
                    <Route path="/doctors/reservation-slots" element={<ReservationSlotManagementPage />} />
                  </Route>
                  <Route path="/doctors/shifts" element={<DoctorShiftListPage />} />
                  <Route path="/doctors/shift-upload" element={<DoctorShiftUploadPage />} />
                  <Route path="/doctors/*" element={<ComingSoon feature="医師管理" />} />
                  
                  {/* レポート（将来実装用のプレースホルダー） */}
                  <Route path="/reports/*" element={<ComingSoon feature="レポート・分析" />} />
                  
                  {/* 設定（将来実装用のプレースホルダー） */}
                  <Route path="/settings/*" element={<ComingSoon feature="システム設定" />} />
                </Route>
              </Route>
              
              {/* デフォルトでダッシュボードにリダイレクト */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              
              {/* 404の場合もダッシュボードにリダイレクト */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </NavigationProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
